/**
 * A client's payment requests: what they have been asked to pay, and what came
 * of each ask.
 *
 * Two endpoints, both hung off the client:
 *
 *   `GET  /api/v1/clients/{public_id}/payment-requests/` — the history, paged
 *   `POST /api/v1/clients/{public_id}/payment-requests/` — raises a new one
 *
 * The `POST` sends only what the dietitian decided — which plan, and what
 * to charge for it. The invoice number, the discount that charge works out to,
 * and the client's details as the invoice records them are all the backend's,
 * and come back on the response. So a raised request is drawn from what was
 * stored rather than from what was sent, the same way `plansService` builds
 * its row out of the response.
 *
 * Money crosses the wire as a decimal string — `"2000.00"` — because that is
 * how the backend keeps it. It is a number everywhere on this side.
 *
 * Settling one is not under the client at all: a request that exists is its own
 * record, confirmed and cancelled through its own `public_id` by calls the
 * enquiry screen makes too. The two wrappers at the foot of this file are how
 * this module reaches them — they are here so the tab has one door, and they
 * turn what comes back into the row it already holds.
 */

import {
  api,
  API_ENDPOINTS,
  isPaymentRequestCancelled,
  isPaymentRequestPaid,
  paymentPlanName,
  toAmountDue,
  toAmountNumber,
  withValidationDetail,
} from "../../../lib/api";
import type {
  CreatePaymentRequestPayload,
  PaginatedResponse,
  PaymentRequestResponse,
} from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import {
  cancelPaymentRequest,
  confirmPaymentRequest,
} from "../../payments/services/paymentRequestsService";
import type {
  ClientPayment,
  ClientPaymentStatus,
  NewClientPayment,
} from "../types/clients.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * What the row reads as, in this screen's three words. Both questions are the
 * API layer's to answer — see `isPaymentRequestPaid` — so a backend state the
 * app has never seen reads as "Not paid", a question still open, rather than
 * as a blank cell.
 */
const toStatus = (response: PaymentRequestResponse): ClientPaymentStatus => {
  if (isPaymentRequestPaid(response)) return "Paid";
  if (isPaymentRequestCancelled(response)) return "Cancelled";
  return "Not paid";
};

/**
 * One row of the history, out of the record either verb answers with.
 *
 * Every field is defended: a row the backend sent with a plan it could not
 * name is still a row the table has to draw, and a dash in a cell is a better
 * answer than a screen that fails to render.
 */
const toClientPayment = (response: PaymentRequestResponse): ClientPayment => ({
  id: response.public_id,
  invoiceNumber: response.invoice_number ?? "",
  planName: paymentPlanName(response) || "-",
  goal: response.goal || "-",
  duration: response.duration_label || "-",
  amount: toAmountNumber(response.amount_due),
  status: toStatus(response),
  generatedAt: response.generated_on,
  // `paid_on` is the day; the receipt carries it too, and is what a row that
  // was paid without the top-level field being set still reads its date from.
  paidAt: response.paid_on ?? response.payment?.received_on ?? null,
  cancelledAt: response.cancelled_at ?? null,
  // The engagement is the record of the two, and the top-level `ends_on` says
  // the same as its end date — so the nested one is read first and the flat
  // field stands in for it, the way `paidAt` reads two fields for one day.
  startsOn: response.engagement?.start_date ?? null,
  endsOn: response.engagement?.end_date ?? response.ends_on ?? null,
});

/**
 * How many rows a page asks for. A hundred, because the tab wants the whole
 * history at once — it searches and totals in the browser — so the fewer round
 * trips the walk below takes, the better.
 */
const PAGE_SIZE = 100;

/**
 * Stops a broken `next` from looping forever. At a hundred a page this is
 * 2,000 requests against one client, which is far past a lifetime of them.
 */
const MAX_PAGES = 20;

/** One page of a client's payment requests, in the order the backend sent them. */
export async function fetchClientPaymentsPage(
  getToken: GetToken,
  clientId: string,
  page?: number,
  signal?: AbortSignal
): Promise<{ payments: ClientPayment[]; hasNext: boolean }> {
  const response = await api.get<
    PaginatedResponse<PaymentRequestResponse>
  >(API_ENDPOINTS.clients.paymentRequests(clientId), {
    token: await token(getToken, "load this client's payments"),
    // Both are optional — left out, the backend serves its own first page.
    // `undefined` entries are dropped by the API client.
    query: { page, page_size: PAGE_SIZE },
    signal,
  });

  return {
    payments: (response?.results ?? []).map(toClientPayment),
    hasNext: !!response?.next,
  };
}

/**
 * A client's whole payment history, newest first.
 *
 * Every page rather than one of them: the tab sums what has come in and
 * searches across the lot, and neither is something a single page can answer.
 * It moves to the server the day the endpoint can total or filter for itself.
 *
 * Sorted here rather than trusted to arrive that way — the tab reads top-down
 * as most-recent-first, and that is the app's decision to make, not one to
 * inherit from whatever order the pages happened to come back in.
 */
export async function fetchClientPayments(
  getToken: GetToken,
  clientId: string,
  signal?: AbortSignal
): Promise<ClientPayment[]> {
  const payments: ClientPayment[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await fetchClientPaymentsPage(
      getToken,
      clientId,
      page,
      signal
    );
    payments.push(...result.payments);

    // An empty page as well as `next: null` ends the walk — a page that says
    // there is another but returns nothing would otherwise spin to the cap.
    if (!result.hasNext || result.payments.length === 0) break;
  }

  return payments.sort(
    (a, b) =>
      new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
  );
}

/**
 * Raises a payment request against a client, and returns the row the backend
 * stored — invoice number, discount and all, none of which the caller knew
 * before it asked.
 *
 * A 400 arrives as an `ApiError` whose message names the fields the backend
 * rejected rather than just "Validation failed"; a 404 means the plan id
 * was not one of the workspace's, which is a catalog that moved under an open
 * dialog.
 */
export async function requestClientPayment(
  getToken: GetToken,
  clientId: string,
  input: NewClientPayment,
  signal?: AbortSignal
): Promise<ClientPayment> {
  const body: CreatePaymentRequestPayload = {
    package: input.planId,
    amount_due: toAmountDue(input.amount),
    // Left off entirely rather than sent empty when there is none: the backend
    // dates the engagement itself, and `""` is not a date it could parse.
    ...(input.startDate ? { start_date: input.startDate } : {}),
  };

  try {
    const response = await api.post<PaymentRequestResponse>(
      API_ENDPOINTS.clients.paymentRequests(clientId),
      body,
      { token: await token(getToken, "request a payment"), signal }
    );

    return toClientPayment(response);
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Marks a payment received, on the day it actually came in.
 *
 * Answers with the row as it now stands rather than with the receipt alone:
 * the endpoint sends back only what was received, and the tab holds a whole
 * history — so the receipt is folded onto the row it settles, and the caller
 * gets back something it can put straight into the list.
 */
export async function confirmClientPayment(
  getToken: GetToken,
  payment: ClientPayment,
  receivedOn: string,
  signal?: AbortSignal
): Promise<ClientPayment> {
  const receipt = await confirmPaymentRequest(
    getToken,
    payment.id,
    receivedOn,
    signal
  );

  return {
    ...payment,
    status: "Paid",
    // The backend's own record of the day, not the one that was sent.
    paidAt: receipt.receivedOn,
  };
}

/**
 * Calls a raised request off, and returns the row as the backend now holds it.
 *
 * Rebuilt from the whole request the endpoint answers with rather than patched
 * onto the row in hand: what makes a row cancelled is the backend's status and
 * its `cancelled_at`, and reading them back is what keeps the tab from showing
 * a state the server did not agree to.
 */
export async function cancelClientPayment(
  getToken: GetToken,
  paymentId: string,
  signal?: AbortSignal
): Promise<ClientPayment> {
  return toClientPayment(
    await cancelPaymentRequest(getToken, paymentId, signal)
  );
}
